import React, { useState } from 'react'
import type { Todo } from '../../api/types'
import { useUpdateTodo } from '../../api/hooks'
import { useToast } from '../primitives/Toast'

interface ReminderOptionsProps {
  todo: Todo
  /** Called after a successful change so the containing popover/sheet can close. */
  onDone?: () => void
  /** Larger touch targets for the mobile action sheet. */
  compact?: boolean
}

function addHours(h: number): string {
  const d = new Date()
  d.setHours(d.getHours() + h, 0, 0, 0)
  return d.toISOString()
}

function tomorrowAt(hour: number): string {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  d.setHours(hour, 0, 0, 0)
  return d.toISOString()
}

/**
 * The single reminder menu, shared by the desktop popover and the mobile sheet.
 *
 * Two kinds of reminder live here. A *relative rule* (day offset + time of day)
 * is re-derived by the server whenever the todo moves to another day, so it only
 * makes sense for a dated todo. An *absolute* reminder is a fixed instant and
 * never moves. Picking one clears the other.
 */
export function ReminderOptions({ todo, onDone, compact = false }: ReminderOptionsProps) {
  const [customTime, setCustomTime] = useState('11:00')
  const [showCustomOnDay, setShowCustomOnDay] = useState(false)
  const [customDate, setCustomDate] = useState('')
  const [customDateTime, setCustomDateTime] = useState('')
  const [showCustomAbsolute, setShowCustomAbsolute] = useState(false)

  const updateTodo = useUpdateTodo()
  const { showToast } = useToast()

  const apply = (
    patch: { reminder_at?: string | null; reminder_time?: string; reminder_day_offset?: number | null },
    removed = false
  ) => {
    updateTodo.mutate(
      { id: todo.id, ...patch },
      {
        onSuccess: () => {
          showToast(removed ? 'Reminder removed' : 'Reminder set', 'success')
          onDone?.()
        },
        onError: () => showToast('Failed to set reminder', 'error'),
      }
    )
  }

  const setRelative = (time: string, dayOffset: number) =>
    apply({ reminder_time: time, reminder_day_offset: dayOffset })

  const setAbsolute = (iso: string) => apply({ reminder_at: iso })

  const remove = () => apply({ reminder_at: '', reminder_time: '' }, true)

  const rowStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    padding: compact ? '8px 0' : '6px 8px',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'var(--font-serif)',
    fontSize: compact ? '0.9rem' : '0.8125rem',
    color: 'var(--color-ink)',
    width: '100%',
  }

  const currentLabel = todo.reminder_time
    ? `${todo.reminder_time}${todo.reminder_day_offset === -1 ? ', day before' : ''}`
    : todo.reminder_at
      ? new Date(todo.reminder_at).toLocaleString()
      : null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <p style={{ fontSize: '0.75rem', color: 'var(--color-ink-muted)', margin: '0 0 4px' }}>
        {currentLabel ? `Reminder: ${currentLabel}` : 'Remind me…'}
      </p>

      {todo.date && (
        <>
          <button style={rowStyle} onClick={() => setRelative('11:00', 0)}>
            Morning of (11:00)
          </button>
          <button style={rowStyle} onClick={() => setRelative('18:00', -1)}>
            Evening before (18:00)
          </button>
          <button style={rowStyle} onClick={() => setShowCustomOnDay((v) => !v)}>
            Custom time on that day…
          </button>
          {showCustomOnDay && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '0 0 4px' }}>
              <input
                type="time"
                value={customTime}
                onChange={(e) => setCustomTime(e.target.value)}
                style={inputStyle}
              />
              <button style={primaryStyle} onClick={() => setRelative(customTime, 0)}>
                Set
              </button>
            </div>
          )}
          <hr className="hairline" style={{ margin: '4px 0' }} />
        </>
      )}

      <button style={rowStyle} onClick={() => setAbsolute(addHours(1))}>
        In 1 hour
      </button>
      <button style={rowStyle} onClick={() => setAbsolute(addHours(3))}>
        In 3 hours
      </button>
      <button style={rowStyle} onClick={() => setAbsolute(tomorrowAt(11))}>
        Tomorrow 11am
      </button>
      <button style={rowStyle} onClick={() => setShowCustomAbsolute((v) => !v)}>
        Custom date &amp; time…
      </button>
      {showCustomAbsolute && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '0 0 4px' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              style={{ ...inputStyle, flex: 1 }}
            />
            <input
              type="time"
              value={customDateTime}
              onChange={(e) => setCustomDateTime(e.target.value)}
              style={{ ...inputStyle, flex: 1 }}
            />
          </div>
          <button
            style={primaryStyle}
            onClick={() => {
              if (!customDate || !customDateTime) return
              setAbsolute(new Date(`${customDate}T${customDateTime}:00`).toISOString())
            }}
          >
            Set reminder
          </button>
        </div>
      )}

      {(todo.reminder_at || todo.reminder_time) && (
        <button style={{ ...rowStyle, color: '#C0392B' }} onClick={remove}>
          Remove reminder
        </button>
      )}
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  background: 'var(--color-paper)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '4px 8px',
  fontFamily: 'var(--font-serif)',
  fontSize: '0.8125rem',
  color: 'var(--color-ink)',
  outline: 'none',
}

const primaryStyle: React.CSSProperties = {
  background: 'var(--color-accent)',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 12px',
  cursor: 'pointer',
  fontFamily: 'var(--font-serif)',
  fontSize: '0.8125rem',
  color: 'white',
}
